import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import SignupMultistep from '../user/SignupMultistep';
import { useNavigate, useSearchParams } from 'react-router';
import { WrapperContext } from '../context/WrapperContext';
import { AppContext } from '../window';
import { PatchCheckFill, PeopleFill, HeartFill } from 'react-bootstrap-icons';
import AuthRedirect from '../components/AuthRedirect';
import ActivityFeed from './components/ActivityFeed';
import OfferCountdown from './components/OfferCountdown';
import TrustBadges from './components/TrustBadges';
import QuizFlow from './components/QuizFlow';
import ExitIntentPopup from './components/ExitIntentPopup';
import { preloadImage } from '../utils/preloadImage';
import { useTranslation } from '../i18n';
import { useTracking } from '../hooks/useTracking';
import { getLandingBackground } from './utils/landingBackground';

const LandingPageB: React.FC = () => {
    const navigate = useNavigate();
    const { setWrapperClasses } = useContext(WrapperContext);
    const { location, whitelabel } = useContext(AppContext);
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const [showQuiz, setShowQuiz] = useState(true);
    const track = useTracking();
    const backgroundSrc = useMemo(() => getLandingBackground(searchParams, whitelabel.wantedGender), [searchParams, whitelabel.wantedGender]);

    // Fire advertiser_page on mount — media-buy landing page view
    useEffect(() => {
        track('advertiser_page');
    }, []);

    // Fire page_interact on first click/tap anywhere on the page
    useEffect(() => {
        const handler = () => { track('page_interact'); };
        document.addEventListener('click', handler, { once: true, capture: true });
        return () => { document.removeEventListener('click', handler, { capture: true }); };
    }, []);

    useEffect(() => {
        setWrapperClasses('100vh d-flex');
        
        return () => {
            setWrapperClasses('');
        };
    }, [setWrapperClasses]);

    useEffect(() => {
        preloadImage(backgroundSrc, 'high');
    }, [backgroundSrc]);

    const handleSignupSuccess = () => {
        return navigate('/search');
    };

    const handleQuizComplete = (answers: any) => {
        // Store answers if needed in the future
        console.log('Quiz completed with answers:', answers);
        setShowQuiz(false);
    };

    const handleExitIntentStay = () => {
        // Scroll to signup form when user chooses to stay
        const signupContainer = document.querySelector('.signup-container-multistep');
        if (signupContainer) {
            signupContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };



    return (
        <div
            className="landing-page-b flex-grow-1 d-flex align-items-center"
            style={{ position: 'relative' }}
        >
            <img
                src={backgroundSrc}
                alt=""
                aria-hidden="true"
                fetchPriority="high"
                decoding="async"
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    zIndex: 0
                }}
            />
            <AuthRedirect />
            <ActivityFeed />
            <ExitIntentPopup onStay={handleExitIntentStay} />
            
            {/* Dark overlay for better text readability */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1
                }}
            />
            
            {/* Offer countdown */}
            <div className="position-absolute top-0 start-0 end-0 p-2 p-md-4 d-flex justify-content-center" style={{ zIndex: 3 }}>
                <OfferCountdown />
            </div>
            
            <Container fluid className="px-3 px-md-5" style={{ position: 'relative', zIndex: 2, paddingTop: '60px', paddingBottom: '2rem' }}>
                <Row className="justify-content-center align-items-center">
                    <Col xxl={10} xl={11}>
                        <Row className="align-items-center gx-5 gy-5">
                            {/* Left side - Quiz or Multi-step signup form */}
                            <Col lg={7} className="order-1 order-lg-1">
                                <div className={`signup-container-multistep bg-white rounded-4 shadow-lg mx-auto ${showQuiz ? 'with-quiz' : ''}`}>
                                    <div className="modal-content">
                                        {showQuiz ? (
                                            <>
                                                <div className="modal-header border-0 pb-0">
                                                    <h2 className="modal-title fs-3 fw-bold text-center w-100">{t('landing_b.quiz_title')}</h2>
                                                </div>
                                                <div className="modal-body pt-4">
                                                    <QuizFlow onComplete={handleQuizComplete} onFirstAnswer={() => track('cta_click')} />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="modal-header border-0 pb-0">
                                                    <h2 className="modal-title fs-3 fw-bold text-center w-100">{t('landing_b.signup_title')}</h2>
                                                </div>
                                                <div className="modal-body pt-4">
                                                    <SignupMultistep
                                                        onSignupSuccess={handleSignupSuccess}
                                                    />
                                                    <TrustBadges className="mt-3" />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Col>
                            
                            {/* Right side - Hero content */}
                            <Col lg={5} className="text-center text-lg-start order-2 order-lg-2">
                                <div className="hero-content text-white">
                                    <h1 className="display-5 fw-bold mb-3 animate-fade-in-up">
                                        {location?.town?.name 
                                            ? t('landing_b.meet_singles_location', { location: location.town.name })
                                            : t('landing_b.meet_singles_your_area')}
                                    </h1>
                                    <p className="lead mb-3 animate-fade-in-up animation-delay-1">
                                        {location?.town?.name && t('landing_b.locals_waiting', { location: location.town.name })} {t('landing_b.join_locals_online', { count: 312 })}
                                        <span className="d-block text-warning fw-bold">{t('landing_b.instant_matches_distance', { distance: 25 })}</span>
                                    </p>
                                    <div className="hero-features ps-3">
                                        <div className="d-flex align-items-center mb-3 animate-fade-in-right animation-delay-2">
                                            <PatchCheckFill className="me-3" size={24} />
                                            <span>{t('landing_b.private_secure')}</span>
                                        </div>
                                        <div className="d-flex align-items-center mb-3 animate-fade-in-right animation-delay-3">
                                            <PeopleFill className="me-3" size={24} />
                                            <span>{location?.town?.name 
                                                ? t('landing_b.connect_with_location', { location: location.town.name })
                                                : t('landing_b.connect_with_local')}</span>
                                        </div>
                                        <div className="d-flex align-items-center animate-fade-in-right animation-delay-4">
                                            <HeartFill className="me-3" size={24} />
                                            <span className="badge bg-danger me-2">{t('landing.new_badge')}</span>
                                            <span>{t('landing.ai_matching')}</span>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default LandingPageB;